import React, { useEffect, useState } from "react";
import { Loader } from "./loader";
import api from "../../api";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  Ticket,
  Clock,
  Settings,
  CheckCircle,
  User,
  CreditCard,
  Cpu,
  Eye,
  AlertCircle,
  MessageSquare,
  Calendar,
  Hash,
  Mail,
  Filter,
  TrendingUp,
  FileText,
  Image as ImageIcon,
  ChevronRight,
  RefreshCw,
  Download,
  Users
} from "lucide-react";
import './DataInfoSupport.css'

export const DataInfoSupport = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);

  const getTickets = async () => {
    try {
      setLoader(true);
      const response = await api.getDataInfoSupport({ page: 1 });
      setData(response);
      setLoader(false);
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  useEffect(() => {
    getTickets();
  }, []);

  return (
    <>
      <div className="info-support-container">
        <div className="info-support-content">
          {/* Header Section */}
          <div className="info-support-header">
            <div className="info-header-content">
              <div className="info-header-main">
                <div className="info-header-icon-wrapper">
                  <div className="info-header-icon-bg">
                    <Ticket size={28} className="info-header-icon" />
                  </div>
                </div>
                <div className="info-header-text">
                  <div className="info-header-top">
                    <h1 className="info-support-title">Support Dashboard</h1>
                    <div className="info-header-badge">
                      <TrendingUp size={14} />
                      <span>{data?.recentUser?.length || 0} new today</span>
                    </div>
                  </div>
                  <p className="info-support-subtitle">
                    Monitor and manage all support tickets
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Grid */}
          <div className="info-analytics-grid">
            <InfoAnalyticsBox
              icon={<Ticket size={22} />}
              title="All Tickets"
              amount={data?.allTicketCount}
              gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
            />
            <InfoAnalyticsBox
              icon={<Clock size={22} />}
              title="Pending"
              amount={data?.pendingTicketCount}
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            />
            <InfoAnalyticsBox
              icon={<Settings size={22} />}
              title="Processing"
              amount={data?.openTicketCount}
              gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
            />
            <InfoAnalyticsBox
              icon={<CheckCircle size={22} />}
              title="Resolved"
              amount={data?.closedTicketCount}
              gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            />
            <InfoAnalyticsBox
              icon={<User size={22} />}
              title="Account Issues"
              amount={data?.accountTicketCount}
              gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
            />
            <InfoAnalyticsBox
              icon={<CreditCard size={22} />}
              title="Payment Issues"
              amount={data?.paymentTicketCount}
              gradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
            />
            <InfoAnalyticsBox
              icon={<Cpu size={22} />}
              title="Technical Issues"
              amount={data?.technicalTicketCount}
              gradient="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
            />
          </div>

          {/* Recent Tickets Section */}
          <div className="info-recent-section">
            <div className="info-section-header">
              <div className="info-section-title">
                <div className="info-section-icon-bg">
                  <MessageSquare size={24} className="info-section-icon" />
                </div>
                <div className="info-section-text">
                  <h3 className="info-section-title-text">Recent Tickets</h3>
                  <p className="info-section-subtitle">Latest support requests from users</p>
                </div>
              </div>
              <button className="info-view-all-btn">
                View All Tickets
                <ChevronRight size={16} />
              </button>
            </div>
            
            <div className="info-content-card">
              <InfoTable data={data?.recentUser ? data?.recentUser : []} />
            </div>
          </div>
        </div>
      </div>
      
      {loader && <Loader />}
      
    </>
  );
};

function InfoAnalyticsBox({ icon, title, amount, gradient }) {
  return (
    <>
      <div className="info-analytics-card">
        <div className="info-analytics-main">
          <div className="info-analytics-icon-wrapper">
            <div className="info-analytics-icon-bg" style={{ background: gradient }}>
              {icon}
            </div>
          </div>
          <div className="info-analytics-content">
            <div className="info-analytics-title">{title}</div>
            <div className="info-analytics-amount">{amount || 0}</div>
          </div>
        </div>
        <div className="info-analytics-trend">
          <span>0%</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="info-trend-icon">
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </>
  );
}

const InfoTable = ({ data }) => {
  const navigate = useNavigate();

  const formatDate = (date) => {
    let dateTemp = moment(date).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return { gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', bg: '#fef3c7', text: '#d97706' };
      case 'processing':
        return { gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', bg: '#f5f3ff', text: '#7c3aed' };
      case 'resolved':
      case 'closed':
        return { gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', bg: '#d1fae5', text: '#059669' };
      default:
        return { gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)', bg: '#f1f5f9', text: '#64748b' };
    }
  };

  const getIssueTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'account':
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
      case 'payment':
        return 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)';
      case 'technical':
        return 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
      default:
        return 'linear-gradient(135deg, #64748b 0%, #475569 100%)';
    }
  };

  return (
    <>
      <div className="info-table-container">
        <div className="info-table-header">
          <div className="info-table-header-item">
            <div className="info-header-icon-wrapper">
              <div className="info-header-icon-bg-sm">
                <Hash size={16} className="info-header-icon-sm" />
              </div>
            </div>
            <span className="info-header-text">Issue ID</span>
          </div>
          <div className="info-table-header-item">
            <div className="info-header-icon-wrapper">
              <div className="info-header-icon-bg-sm">
                <ImageIcon size={16} className="info-header-icon-sm" />
              </div>
            </div>
            <span className="info-header-text">Image</span>
          </div>
          <div className="info-table-header-item">
            <div className="info-header-icon-wrapper">
              <div className="info-header-icon-bg-sm">
                <Mail size={16} className="info-header-icon-sm" />
              </div>
            </div>
            <span className="info-header-text">User Email</span>
          </div>
          <div className="info-table-header-item">
            <div className="info-header-icon-wrapper">
              <div className="info-header-icon-bg-sm">
                <AlertCircle size={16} className="info-header-icon-sm" />
              </div>
            </div>
            <span className="info-header-text">Status</span>
          </div>
          <div className="info-table-header-item">
            <div className="info-header-icon-wrapper">
              <div className="info-header-icon-bg-sm">
                <Filter size={16} className="info-header-icon-sm" />
              </div>
            </div>
            <span className="info-header-text">Issue Type</span>
          </div>
          <div className="info-table-header-item">
            <div className="info-header-icon-wrapper">
              <div className="info-header-icon-bg-sm">
                <Calendar size={16} className="info-header-icon-sm" />
              </div>
            </div>
            <span className="info-header-text">Created At</span>
          </div>
          <div className="info-table-header-item">
            <div className="info-header-icon-wrapper">
              <div className="info-header-icon-bg-sm">
                <Eye size={16} className="info-header-icon-sm" />
              </div>
            </div>
            <span className="info-header-text">Actions</span>
          </div>
        </div>
        
        <div className="info-table-body">
          {data.length > 0 ? (
            data.map((item, key) => (
              <div key={key} className="info-table-row">
                <div className="info-table-cell">
                  <div className="info-issue-id">
                    <Hash size={14} className="info-cell-icon" />
                    <span className="info-issue-id-text">{item?.issueId}</span>
                  </div>
                </div>
                <div className="info-table-cell">
                  {item?.imageTitle ? (
                    <div className="info-image-container">
                      <div className="info-image-preview">
                        <img
                          src={`${process.env.REACT_APP_S3URL}/img_${item?.imageTitle}.${item.imageFormat}`}
                          alt="Ticket attachment"
                          className="info-ticket-image"
                        />
                        <div className="info-image-overlay">
                          <Eye size={12} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="info-no-image">
                      <span>No Image</span>
                    </div>
                  )}
                </div>
                <div className="info-table-cell">
                  <div className="info-user-email">
                    <Mail size={14} className="info-cell-icon" />
                    <span className="info-email-text">{item?.userId?.email || "-"}</span>
                  </div>
                </div>
                <div className="info-table-cell">
                  <div className="info-status-container">
                    <div 
                      className="info-status-badge"
                      style={{ 
                        background: getStatusColor(item?.status).bg,
                        color: getStatusColor(item?.status).text
                      }}
                    >
                      <div 
                        className="info-status-icon-bg"
                        style={{ background: getStatusColor(item?.status).gradient }}
                      >
                        <AlertCircle size={10} className="info-status-icon" />
                      </div>
                      <span>{item?.status?.charAt(0).toUpperCase() + item?.status?.slice(1)}</span>
                    </div>
                  </div>
                </div>
                <div className="info-table-cell">
                  <div className="info-issue-type">
                    <div 
                      className="info-issue-type-icon-bg"
                      style={{ background: getIssueTypeColor(item?.issueType) }}
                    >
                      <Filter size={12} className="info-issue-type-icon" />
                    </div>
                    <span className="info-issue-type-text" style={{ 
                      background: getIssueTypeColor(item?.issueType),
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {item?.issueType || "-"}
                    </span>
                  </div>
                </div>
                <div className="info-table-cell">
                  <div className="info-created-at">
                    <div className="info-created-icon-bg">
                      <Calendar size={12} className="info-created-icon" />
                    </div>
                    <span className="info-created-text">{formatDate(item?.createdAt)}</span>
                  </div>
                </div>
                <div className="info-table-cell">
                  <div className="info-actions">
                    <button 
                      className="info-view-button"
                      onClick={() => navigate(`/admin/support/ticket/${item?.issueId}`)}
                      title="View Ticket Details"
                    >
                      <div className="info-view-icon-bg">
                        <Eye size={14} className="info-view-icon" />
                      </div>
                      <span className="info-view-text">View</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="info-empty-state">
              <div className="info-empty-icon-wrapper">
                <div className="info-empty-icon-bg">
                  <Ticket size={32} className="info-empty-icon" />
                </div>
              </div>
              <h3 className="info-empty-title">No Recent Tickets</h3>
              <p className="info-empty-description">No support tickets have been created recently</p>
              <button className="info-empty-action">
                <Users size={16} />
                Check All Tickets
              </button>
            </div>
          )}
        </div>
        
        {data.length > 0 && (
          <div className="info-table-footer">
            <div className="info-table-summary">
              <div className="info-summary-item">
                <span className="info-summary-label">Total Tickets:</span>
                <span className="info-summary-value">{data.length}</span>
              </div>
              <div className="info-summary-item">
                <span className="info-summary-label">Last Updated:</span>
                <span className="info-summary-value">{moment().format('MMMM Do, h:mm A')}</span>
              </div>
            </div>
            <div className="info-table-actions">
              <button className="info-export-btn">
                <div className="info-export-icon-bg">
                  <Download size={14} className="info-export-icon" />
                </div>
                Export CSV
              </button>
              <button className="info-refresh-btn">
                <div className="info-refresh-icon-bg">
                  <RefreshCw size={14} className="info-refresh-icon" />
                </div>
                Refresh
              </button>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        
      `}</style>
    </>
  );
};